import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Observable, of, Subject } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ProductV1 } from 'src/app/models/storage/product.model';
import { Categories } from 'src/app/models/storage/categories.model'
import { Discounts } from 'src/app/models/storage/discounts.model';
import { CurrentUserService } from './current-user.service';
import { LogService } from './log.service';
import { ProductSizeType } from 'src/app/models/storage/product-size-type.model';
import firestore from 'firebase/compat/app';
import { DocumentChangeType } from 'firebase/firestore';
import { isValidTipPreset } from 'src/app/utils/tip-preset.util';

export const GLOBAL_DEFAULT_SIZE_TYPE_ID = 'nJaaXROMVSfPEEU7JXOS';

@Injectable({
  providedIn: 'root',
})
export class ProductsService {
  constructor(
    private afs: AngularFirestore,
    private currentUserService: CurrentUserService,
    private _logService: LogService
  ) { }

  private lastDocument: number = 0;
  private countZones = 0;
  private zoneIDs: string[] = [];
  public date = new Date();
  public startday: any = new Date(
    this.date.getFullYear(),
    this.date.getMonth(),
    this.date.getDate() - 1
  );

  public triggerGetProducts = new Subject();

  public inventoryPages: any = [];

  getProductsSubject() {
    this.triggerGetProducts.next('');
  }

  getProductsManually(status?) {
    return this.afs
      .collection('products', (ref) =>
        ref
          .where(
            'companyID',
            '==',
            this.currentUserService.currentUser.companyId
          )
          .where('isRetired', '==', false)
          .orderBy('productName')
          .orderBy('productSize')
      )
      .snapshotChanges()
      .pipe(
        map((changes) => {
          return changes.map((action) => {
            let data = action.payload.doc.data() as ProductV1;
            data.id = action.payload.doc.id;
            return data;
          });
        })
      );
  }

  getProductsInventory(status?): Observable<ProductV1[]> {
    return this.afs
      .collection('products', (ref) =>
        ref
          .where(
            'companyID',
            '==',
            this.currentUserService.currentUser.companyId
          )
          .where('isActive', '==', true)
          .orderBy('productName')
          .orderBy('productSize')
      )
      .snapshotChanges()
      .pipe(
        map((changes) => {
          return changes.map((action) => {
            let data = action.payload.doc.data() as ProductV1;
            data.id = action.payload.doc.id;
            return data;
          });
        })
      );
  }
  getProducts(status?): Observable<ProductV1[]> {
    return this.afs
      .collection('products', (ref) =>
        ref
          .where(
            'companyID',
            '==',
            this.currentUserService.currentUser.companyId
          )
          .where('isActive', '==', true)
          .orderBy('productName')
          .orderBy('productSize')
      )
      .snapshotChanges()
      .pipe(
        map((changes) => {
          return changes.map((action) => {
            let data = action.payload.doc.data() as ProductV1;
            data.id = action.payload.doc.id;
            return data;
          });
        })
      );
  }

  /**
   * One-shot Promise equivalent of {@link getProducts} that bypasses the
   * snapshotChanges cache race.
   *
   * `firstValueFrom(getProducts())` takes the first snapshot emission, which
   * Firestore fires from local cache before the server response — partial /
   * stale subsets show up and the listener unsubscribes before the server
   * fills in. This method uses `.ref.get()` for a single server-authoritative
   * read with the same filters as `getProducts`, so callers that want a
   * promise (e.g. waiver-builder's Attach modal) get the full set every time.
   */
  async getActiveProductsForCompanyPromise(): Promise<ProductV1[]> {
    const snap = await this.afs
      .collection('products')
      .ref
      .where('companyID', '==', this.currentUserService.currentUser.companyId)
      .where('isActive', '==', true)
      .orderBy('productName')
      .orderBy('productSize')
      .get();
    return snap.docs.map((d) => {
      const data = d.data() as ProductV1;
      data.id = d.id;
      return data;
    });
  }

  getProductsWithParamCompanyID(companyID: string) {
    return this.afs
      .collection('products', (ref) =>
        ref
          .where('companyID', '==', companyID)
          .where('isActive', '==', true)
          .where('isAvailable', '==', true)
          .orderBy('productName')
          .orderBy('productSize')
      )
      .snapshotChanges()
      .pipe(
        map((changes) => {
          return changes.map((action) => {
            let data = action.payload.doc.data() as ProductV1;
            data.id = action.payload.doc.id;
            return data;
          });
        })
      );
  }

  getAllByProducts(status?) {
    return new Promise<ProductV1[]>((resolve, reject) => {
      const refRelaseNote = this.afs
        .collection('products', (ref) =>
          ref
            .where(
              'companyID',
              '==',
              this.currentUserService.currentUser.companyId
            )
            .where('isActive', '==', true)
            .where('isAvailable', '==', true)
            .orderBy('productName')
            .orderBy('productSize')
        )
        .valueChanges({ idField: 'id' });

      refRelaseNote
        .forEach((data: any) => {
          resolve(data);
        })
        .catch((err) => reject(err));
    });
  }
  validateWidgetsValue(widget: any): boolean {
    let validate = false;
    // Tip filter
    if (widget.widgetType === "tip") {
      const element = widget.element;
      if (element && element.is_required === false) {
        // Optional tip: usable only if it offers at least one valid preset percentage
        // OR allows a custom amount. (Its fixed `price` is intentionally empty.)
        const hasValidPreset = Array.isArray(element.presets) && element.presets.some(isValidTipPreset);
        validate = hasValidPreset || element.allowCustom === true;
      } else {
        // Required (or legacy) tip: unchanged rule — a positive fixed percentage.
        validate = !!(element && element.price > 0 && element.price != null);
      }
    }else{
      //For more addons if required later
      validate = true;
    }
    // Return value
    return validate;
  }
  getMaintenanceProducts(): Observable<ProductV1[]> {
    return this.afs
      .collection('products', (ref) =>
        ref
          .where(
            'companyID',
            '==',
            this.currentUserService.currentUser.companyId
          )
          .where('isRetired', '==', false)
          .where('isMaintenance', '==', true)
          .orderBy('productName')
          .orderBy('productSize')
      )
      .snapshotChanges()
      .pipe(
        map((changes) => {
          return changes.map((action) => {
            let data = action.payload.doc.data() as ProductV1;
            data.id = action.payload.doc.id;
            return data;
          });
        })
      );
  }

  getUnavailableProducts(): Observable<ProductV1[]> {
    return this.afs
      .collection('products', (ref) =>
        ref
          .where(
            'companyID',
            '==',
            this.currentUserService.currentUser.companyId
          )
          .where('isRetired', '==', false)
          .where('isAvailable', '==', false)
          .orderBy('productName')
          .orderBy('productSize')
      )
      .snapshotChanges()
      .pipe(
        map((changes) => {
          return changes.map((action) => {
            let data = action.payload.doc.data() as ProductV1;
            data.id = action.payload.doc.id;
            return data;
          });
        })
      );
  }

  getProductsByCompanyID(): Observable<ProductV1[]> {
    return this.afs
      .collection('products', (ref) =>
        ref
          .where(
            'companyID',
            '==',
            this.currentUserService.currentUser.companyId
          )
          .where('isActive', '==', true)
      )
      .snapshotChanges()
      .pipe(
        map((changes) => {
          return changes.map((action) => {
            let data = action.payload.doc.data() as ProductV1;
            data.id = action.payload.doc.id;
            return data;
          });
        })
      );
  }

  getAffiliates() {
    return this.afs
      .collection('affiliates', (ref) =>
        ref.where(
          'companyID',
          '==',
          this.currentUserService.currentUser.companyId
        )
      )
      .snapshotChanges()
      .pipe(
        map((changes) => {
          return changes.map((action) => {
            let data = action.payload.doc.data() as ProductV1;
            data.id = action.payload.doc.id;
            return data;
          });
        })
      );
  }

  getProductsPromise() {
    let ref = this.afs
      .collection('products')
      .ref.where(
        'companyID',
        '==',
        this.currentUserService.currentUser.companyId
      );
    return ref.get().then((res) => {
      return res.docs.map((r) => {
        let data = r.data() as ProductV1;
        data.id = r.id;
        return data;
      });
    });
  }

  // Subscribe to product collection based off companyID passed in param - useful on non partner routes (for now while we don't utilize location for products)
  getProductsByCompanyIDParam(companyID): Observable<ProductV1[]> {
    return this.afs
      .collection('products', (ref) =>
        ref
          .where('companyID', '==', companyID)
          .where('isActive', '==', true)
          .orderBy('productName')
          .orderBy('productSize')
      )
      .snapshotChanges()
      .pipe(
        map((changes) => {
          return changes.map((action) => {
            let data = action.payload.doc.data() as ProductV1;
            data.id = action.payload.doc.id;
            return data;
          });
        })
      );
  }

  getNewProduct(zoneID: string): Observable<ProductV1[]> {
    return this.afs
      .collection('products', (ref) =>
        ref
          .limit(10)
          .where('zoneID', '==', zoneID)
          .startAfter(this.lastDocument)
      )
      .snapshotChanges()
      .pipe(
        map((changes) => {
          return changes.map((action) => {
            let data = action.payload.doc.data() as ProductV1;
            data.id = action.payload.doc.id;
            this.lastDocument = this.lastDocument + 1;
            return data;
          });
        })
      );
  }

  saveNewProduct(product: any) {
    return this.afs.collection('products').add(product);
  }

  saveNewGroup(group: any) {
    return this.afs.collection('productGroup').add(group);
  }
  editProductGroup(group: any) {
    return this.afs.collection('productGroup').doc(group.id).update(group);
  }

  getProductGroups() {
    return this.afs
      .collection('productGroup', (ref) =>
        ref
          .where(
            'companyID',
            '==',
            this.currentUserService.currentUser.companyId
          )
          .where('isActive', '==', true)
          .orderBy('groupName', 'asc')
      )
      .snapshotChanges()
      .pipe(
        map((changes) => {
          return changes.map((action) => {
            let data = action.payload.doc.data() as any;
            data.id = action.payload.doc.id;
            return data;
          });
        })
      );
  }
  getAllProductsGroup() {
    let ref = this.afs
      .collection('productGroup')
      .ref.orderBy('groupName', 'asc');
    return ref.get().then((res) => {
      return res.docs.map((r) => {
        let data = r.data() as any;
        data.id = r.id;
        return data;
      });
    });
  }
  getAllProductsGroupByCompanyPromise() {
    let ref = this.afs
      .collection('productGroup')
      .ref.where("companyID", "==", this.currentUserService.currentUser.companyId).orderBy('groupName', 'asc');
    return ref.get().then((res) => {
      return res.docs.map((r) => {
        let data = r.data() as any;
        data.id = r.id;
        return data;
      });
    });
  }
  getProductGroupsChangesByCompanyID(companyID): Observable<any> {
    return this.afs
      .collection('productGroup', (ref) =>
        ref.where('companyID', '==', companyID).where('isActive', '==', true)
      )
      .stateChanges()
      .pipe(
        map((changes) => {
          return changes.map((action) => {
            let data = action.payload.doc.data() as any;
            data.id = action.payload.doc.id;
            let documentChangeType: DocumentChangeType = action.payload.type;
            return {doc: data, documentChangeType: documentChangeType};
          });
        })
      );
  }

  getProductGroupsByCompanyID(companyID): Observable<any> {
    return this.afs
      .collection('productGroup', (ref) =>
        ref.where('companyID', '==', companyID).where('isActive', '==', true)
      )
      .snapshotChanges()
      .pipe(
        map((changes) => {
          return changes.map((action) => {
            let data = action.payload.doc.data() as any;
            data.id = action.payload.doc.id;
            return data;
          });
        })
      );
  }

  getProductByGroupID(groupID: string) {
    let query = this.afs
      .collection('products')
      .ref.where('productGroupID', '==', groupID)
      .where('isActive', '==', true)
      .where('companyID', '==', this.currentUserService.currentUser.companyId);

    return query.get().then((res) => {
      return res.docs.map((r) => {
        let data = r.data() as any;
        data.id = r.id;
        return data;
      });
    });
  }

  getProductByGroupIDWithParamCompanyID(groupID: string, companyID: string) {
    let query = this.afs
      .collection('products')
      .ref.where('productGroupID', '==', groupID)
      .where('isActive', '==', true)
      .where('companyID', '==', companyID);

    return query.get().then((res) => {
      return res.docs.map((r) => {
        let data = r.data() as any;
        data.id = r.id;
        return data;
      });
    });
  }

  searchProductNumber(productNumber: any) {
    //get one time product
    let query = this.afs
      .collection('products')
      .ref.where('productNumber', '==', productNumber)
      .where('companyID', '==', this.currentUserService.currentUser.companyId);

    return query.get().then((res) => {
      return res.docs.map((r) => {
        let data = r.data() as ProductV1;
        data.id = r.id;
        return data;
      });
    });
  }

  // getAllActiveGroupsForCompany(): Observable<any>{
  //   return this.afs.collection('groups', ref => ref
  //     .where('companyID', '==', this.currentUserService.currentUser.companyId)
  //     .where('isRetired', '==', false))
  //     .snapshotChanges().pipe(map(changes => {
  //       return changes.map(action => {
  //         console.log(action);
  //         let data = action.payload.doc.data() as Product
  //         data.id = action.payload.doc.id
  //         return data
  //       })
  //     }));
  // }

  // searchGroupName(groupName: any) { // search by groupName & current company
  //   let query = this.afs.collection('groups').ref.where('groupName', '==', groupName)
  //     .where('companyID', '==', this.currentUserService.currentUser.companyId)

  //   return query.get().then(res => {
  //     return res.docs.map(r => {
  //       let data = r.data() as Product
  //       data.id = r.id
  //       return {companyID: this.currentUserService.currentUser.companyId, data: data} // returns currentcompanyID & data
  //     })
  //   })
  // }

  // saveNewGroup(group: any) { // creates a new group document & returns it's ID
  //   return this.afs.collection("groups").add(group)
  // .then(docRef => {
  //     return docRef.id;
  // })
  // }

  // updateGroup(id: string, group: any) { // Updates a group doc
  //   return this.afs.collection('groups').doc(id).update(group)
  // }

  async renameGroupNameOnProduct(groupID, oldGroupName, newGroupName, action) {
    if (action == 'edit') {
      // set up as a transaction
      this.afs.firestore.runTransaction((transaction) => {
        // get all products where one value doesn't match the old value + companyID of current user
        return this.afs
          .collection('products')
          .ref.where('groupName', '==', oldGroupName)
          .where('groupID', '==', groupID)
          .where(
            'companyID',
            '==',
            this.currentUserService.currentUser.companyId
          )
          .get()
          .then((snapshot) => {
            snapshot.forEach((snap) => {
              // for every result returned, provide a transaction update to its reference
              transaction.update(snap.ref, { groupName: newGroupName });
            });
          });
      });
    }

    if (action == 'delete') {
      this.afs.firestore.runTransaction((transaction) => {
        // get all products where one value doesn't match the old value + companyID of current user
        return this.afs
          .collection('products')
          .ref.where('groupName', '==', oldGroupName)
          .where('groupID', '==', groupID)
          .where(
            'companyID',
            '==',
            this.currentUserService.currentUser.companyId
          )
          .get()
          .then((snapshot) => {
            snapshot.forEach((snap) => {
              // for every result returned, provide a transaction update to its reference
              transaction.update(snap.ref, { groupName: '', groupID: '' });
            });
          });
      });
    }
  }

  async processGroupChangesToProducts(changes, groupID, groupName) {
    // use a bulkwrite (no need for transaction because we aren't reading info as well)

    let batch = this.afs.firestore.batch();

    // console.log(changes);
    // console.log(groupID);
    // console.log(groupName);

    changes.forEach((change) => {
      if (change.action === 'add') {
        // if add
        // console.log("add")
        batch.update(this.afs.collection('products').doc(change.id).ref, {
          groupID: groupID,
          groupName: groupName,
        });
      }

      if (change.action === 'remove') {
        // if remove
        // console.log("remove")
        batch.update(this.afs.collection('products').doc(change.id).ref, {
          groupID: '',
          groupName: '',
        });
      }
    });

    // console.log(batch);
    await batch.commit();
  }

  searchProductID(id: any) {
    return this.afs
      .collection('products')
      .doc(id)
      .ref.get()
      .then((doc) => {
        const data = doc.data() as any;
        data.id = doc.id;
        return data;
      });
  }

  searchProductIDAndCompanyID(id) {
    console.log(id);
    console.log(this.currentUserService.currentUser.companyId);
    let snapshot = this.afs
      .collection('products')
      .ref.where('__name__', '==', id)
      .where('companyID', '==', this.currentUserService.currentUser.companyId)
      .get()
      .then((snapshot) => {
        return snapshot;
      });
    return snapshot;
  }

  updateProduct(id: string, product: any) {
    return this.afs.collection('products').doc(id).update(product);
  }

  updateMaintenanceProduct(product: ProductV1) {
    console.log('product -->', product);

    return this.afs.collection('products').doc(product.id).update({
      isActive: product.isActive,
      isAvailable: product.isAvailable,
      isDamaged: product.isDamaged,
      isTuneUp: product.isTuneUp,
      isCleaning: product.isCleaning,
      isMaintenance: product.isMaintenance,
    });
  }

  getCompanyProducts() {
    return this.afs
      .collection('products', (ref) =>
        ref.where(
          'companyID',
          '==',
          this.currentUserService.currentUser.companyId
        )
      )
      .snapshotChanges()
      .pipe(
        map((changes) => {
          return changes.map((action) => {
            let data = action.payload.doc.data() as ProductV1;
            data.id = action.payload.doc.id;
            return data;
          });
        })
      );
  }

  getProductsByCategoryID(categoryID: string) {
    let ref = this.afs
      .collection('products')
      .ref.where('categoryID', '==', categoryID);
    return ref.get().then((res) => {
      return res.docs.map((r) => {
        let data = r.data() as any;
        data.id = r.id;
        return data;
      });
    });
  }

  getProductByName(productName: string) {
    let ref = this.afs
      .collection('products')
      .ref.where('productName', '==', productName);
    return ref.get().then((res) => {
      return res.docs.map((r) => {
        let data = r.data() as any;
        data.id = r.id;
        return data;
      });
    });
  }

  getProductByID(productID: string): Promise<ProductV1> {
    return this.afs
      .collection('products')
      .doc(productID)
      .ref.get()
      .then((doc) => {
        const data = doc.data() as ProductV1;
        data.id = doc.id;
        return data;
      });
  }

  getCompanyCategoriesByPromise() {
    const ref = this.afs
      .collection('rentalCategories')
      .ref.where(
        'companyID',
        '==',
        this.currentUserService.currentUser.companyId
      );
    return ref.get().then((res) => {
      return res.docs.map((r) => {
        const data = r.data() as Categories
        data.id = r.id;
        return data;
      });
    });
  }

  getCompanyProductsByCategorie(catid, status): Observable<ProductV1[]> {
    if (status.isActive) {
      return this.afs
        .collection('products', (ref) =>
          ref
            .where('categoryID', '==', catid)
            .where(
              'companyID',
              '==',
              this.currentUserService.currentUser.companyId
            )
            .where('isActive', '==', true)
        )
        .snapshotChanges()
        .pipe(
          map((changes) => {
            return changes.map((action) => {
              let data = action.payload.doc.data() as ProductV1;
              data.id = action.payload.doc.id;
              return data;
            });
          })
        );
    } else if (status.isRetired) {
      return this.afs
        .collection('products', (ref) =>
          ref
            .where('categoryID', '==', catid)
            .where(
              'companyID',
              '==',
              this.currentUserService.currentUser.companyId
            )
            .where('isRetired', '==', true)
        )
        .snapshotChanges()
        .pipe(
          map((changes) => {
            return changes.map((action) => {
              let data = action.payload.doc.data() as ProductV1;
              data.id = action.payload.doc.id;
              return data;
            });
          })
        );
    } else if (status.isDamaged) {
      return this.afs
        .collection('products', (ref) =>
          ref
            .where('categoryID', '==', catid)
            .where(
              'companyID',
              '==',
              this.currentUserService.currentUser.companyId
            )
            .where('isDamaged', '==', true)
            .where('isActive', '==', true)
        )
        .snapshotChanges()
        .pipe(
          map((changes) => {
            return changes.map((action) => {
              let data = action.payload.doc.data() as ProductV1;
              data.id = action.payload.doc.id;
              return data;
            });
          })
        );
    } else if (status.isTuneUp) {
      return this.afs
        .collection('products', (ref) =>
          ref
            .where('categoryID', '==', catid)
            .where(
              'companyID',
              '==',
              this.currentUserService.currentUser.companyId
            )
            .where('isTuneUp', '==', true)
            .where('isActive', '==', true)
        )
        .snapshotChanges()
        .pipe(
          map((changes) => {
            return changes.map((action) => {
              let data = action.payload.doc.data() as ProductV1;
              data.id = action.payload.doc.id;
              return data;
            });
          })
        );
    } else if (status.isCleaning) {
      return this.afs
        .collection('products', (ref) =>
          ref
            .where('categoryID', '==', catid)
            .where(
              'companyID',
              '==',
              this.currentUserService.currentUser.companyId
            )
            .where('isCleaning', '==', true)
            .where('isActive', '==', true)
        )
        .snapshotChanges()
        .pipe(
          map((changes) => {
            return changes.map((action) => {
              let data = action.payload.doc.data() as ProductV1;
              data.id = action.payload.doc.id;
              return data;
            });
          })
        );
    } else if (status.isMaintenance) {
      return this.afs
        .collection('products', (ref) =>
          ref
            .where('categoryID', '==', catid)
            .where(
              'companyID',
              '==',
              this.currentUserService.currentUser.companyId
            )
            .where('isMaintenance', '==', true)
            .where('isActive', '==', true)
        )
        .snapshotChanges()
        .pipe(
          map((changes) => {
            return changes.map((action) => {
              let data = action.payload.doc.data() as ProductV1;
              data.id = action.payload.doc.id;
              return data;
            });
          })
        );
    } else {
      return this.afs
        .collection('products', (ref) =>
          ref
            .where('categoryID', '==', catid)
            .where(
              'companyID',
              '==',
              this.currentUserService.currentUser.companyId
            )
        )
        .snapshotChanges()
        .pipe(
          map((changes) => {
            return changes.map((action) => {
              let data = action.payload.doc.data() as ProductV1;
              data.id = action.payload.doc.id;
              return data;
            });
          })
        );
    }
  }

  getAllProducts(): Observable<any> {
    return this.afs
      .collection('products', (ref) =>
        ref.where(
          'companyID',
          '==',
          this.currentUserService.currentUser.companyId
        )
      )
      .snapshotChanges();
  }

  getAllProductsPayload(): Observable<any> {
    return this.afs
      .collection('products', (ref) =>
        ref.where(
          'companyID',
          '==',
          this.currentUserService.currentUser.companyId
        )
      )
      .snapshotChanges()
      .pipe(
        map((changes) => {
          return changes.map((action) => {
            let data = action.payload.doc.data() as ProductV1;
            data.id = action.payload.doc.id;
            return data;
          });
        })
      );
  }

  private lastInventoryOption: any;

  getFullProducts() {
    let ref = this.afs
      .collection('products')
      .ref.where('companyID', '==', this.currentUserService.currentUser.companyId);
    return ref.get().then((res) => {
      return res.docs.map((r) => {
        let data = r.data() as any;
        data.id = r.id;
        return data;
      });
    });
  }

  searchDeleteCategories(id: string) {
    let ref = this.afs
      .collection('products')
      .ref.where(
        'companyID',
        '==',
        this.currentUserService.currentUser.companyId
      )
      .where('categoryID', '==', id)
      .where('isActive', '==', true);
    return ref.get().then((res) => {
      return res.docs.map((r) => {
        let data = r.data() as any;
        data.id = r.id;
        return data;
      });
    });
  }

  searchDeleteProducts() {
    let ref = this.afs
      .collection('rentals')
      .ref.where(
        'companyID',
        '==',
        this.currentUserService.currentUser.companyId
      );
    return ref.get().then((res) => {
      return res.docs.map((r) => {
        let data = r.data() as any;
        data.id = r.id;
        return data;
      });
    });
  }

  deleteProductGroup(id: any) {
    return this.afs.collection('productGroup').doc(id).delete();
  }

  deleteCategory(id: any) {
    return this.afs.collection('rentalCategories').doc(id).delete();
  }

  deleteProduct(id: any) {
    return this.afs.collection('products').doc(id).delete();
  }
  deleteActiveProduct(id: any) {
    return this.afs.collection('products').doc(id).update({ isActive: false, lastModified: new Date() });
  }
  getProductsValidate(productNumber): Promise<any> {
    let ref = this.afs
      .collection('products')
      .ref.where(
        'companyID',
        '==',
        this.currentUserService.currentUser.companyId
      )
      .where('productNumber', '==', productNumber);
    return ref.get().then((res) => {
      return res.docs.map((r) => {
        let data = r.data() as any;
        data.id = r.id;
        return data;
      });
    });
  }
  getMaxProductNumber() {
    let ref = this.afs
      .collection('products')
      .ref.where(
        'companyID',
        '==',
        this.currentUserService.currentUser.companyId
      )
      .orderBy('productNumber', 'desc')
      .limit(1);
    return ref.get().then((res) => {
      return res.docs.map((r) => {
        let data = r.data() as any;
        data.id = r.id;
        return data;
      });
    });
  }

  private lastInventoryCategory: any;

  getCompanyCategoriesWithScrollRebuild(reset = false): Observable<Categories[]> {
    return this.afs
      .collection('rentalCategories', (ref) =>
        ref.where(
          'companyID',
          '==',
          this.currentUserService.currentUser.companyId
        )
      )
      .snapshotChanges()
      .pipe(
        map((changes) => {
          return changes.map((doc) => {
            let data = doc.payload.doc.data() as any;
            data.id = doc.payload.doc.id;
            return data;
          });
        })
      );
  }

  getCompanyCategories(): Observable<Categories[]> {
    return this.afs
      .collection('rentalCategories', (ref) =>
        ref.where(
          'companyID',
          '==',
          this.currentUserService.currentUser.companyId
        ).where(
          'isRebuild',
          '==',
          false
        )
      )
      .snapshotChanges()
      .pipe(
        map((changes) => {
          return changes.map((doc) => {
            let data = doc.payload.doc.data() as any;
            data.id = doc.payload.doc.id;
            return data;
          });
        })
      );
  }

  saveNewCategory(option: any) {
    return this.afs.collection('rentalCategories').add(option);
  }

  updateCategory(id: string, option: any) {
    return this.afs.collection('rentalCategories').doc(id).update(option);
  }

  getRentalCategories(companyID: string) {
    let ref = this.afs
      .collection('rentalCategories')
      .ref.where('companyID', '==', companyID).where('isRebuild', '==', false);
    return ref.get().then((res) => {
      return res.docs.map((r) => {
        let data = r.data() as any;
        data.id = r.id;
        return data;
      });
    });
  }
  getRentalCategoriesRebuild(companyID: string) {
    let ref = this.afs
      .collection('rentalCategories')
      .ref.where('companyID', '==', companyID);
    return ref.get().then((res) => {
      return res.docs.map((r) => {
        let data = r.data() as any;
        data.id = r.id;
        return data;
      });
    });
  }

  getAllCategoriesByPromise() {
    let ref = this.afs
      .collection('rentalCategories')
      .ref;
    return ref.get().then((res) => {
      return res.docs.map((r) => {
        let data = r.data() as any;
        data.id = r.id;
        return data;
      });
    });
  }
  getCategoryByID(categoryID: string): Promise<any> {
    return this.afs
      .collection('rentalCategories')
      .doc(categoryID)
      .ref.get()
      .then((doc) => {
        const data = doc.data() as any;
        data.id = doc.id;
        return data;
      });
  }

  updateCheckoutOption(id: string, obj: any) {
    return this.afs.collection('checkoutOptions').doc(id).update(obj);
  }

  newCheckoutOption(obj: any) {
    return this.afs.collection('checkoutOptions').add(obj);
  }

  deleteCheckoutOption(id: any) {
    return this.afs.collection('checkoutOptions').doc(id).delete();
  }

  getDiscounts(): Observable<Discounts[]> {
    return this.afs
      .collection('discountCodes', (ref) =>
        ref.where(
          'companyID',
          '==',
          this.currentUserService.currentUser.companyId
        )
      )
      .snapshotChanges()
      .pipe(
        map((changes) => {
          return changes.map((action) => {
            let data = action.payload.doc.data() as Discounts;
            data.id = action.payload.doc.id;
            return data;
          });
        })
      );
  }

  async getDiscountByName(
    discountTitle: string,
    companyID
  ): Promise<Discounts | null> {
    const collection = this.afs
      .collection('discountCodes')
      .ref.where('companyID', '==', companyID);

    const res = await collection.get();

    if (res.size === 0) {
      return null;
    }

    const titleUpp = discountTitle.toUpperCase();

    const discountDoc = res.docs.find((d) => {
      const data = d.data() as Discounts;
      const discountUpp = data.discountTitle.toUpperCase();
      return discountUpp === titleUpp;
    });

    if (discountDoc) {
      const data = discountDoc.data() as Discounts;
      data.id = discountDoc.id;
      return data;
    }

    return null;
  }

  getDiscountByID(discountID: string): Promise<Discounts> {
    if (discountID !== undefined) {
      return this.afs
        .collection('discountCodes')
        .doc(discountID)
        .ref.get()
        .then((doc) => {
          const data = doc.data() as Discounts;
          data.id = doc.id;
          return data;
        });
    } else {
      return new Promise((resolve) => {
        resolve(null);
      });
    }
  }

  saveDiscountCode(obj: any) {
    return this.afs.collection('discountCodes').add(obj);
  }

  updateDiscountCode(id: string, obj: any) {
    return this.afs.collection('discountCodes').doc(id).update(obj);
  }

  private lastCheckoutoption: any;

  getCompanyCheckoutOptionsWithScroll(reset = false): Observable<any[]> {
    return this.afs
      .collection('checkoutOptions', (ref) =>
        ref.where(
          'companyID',
          '==',
          this.currentUserService.currentUser.companyId
        )
      )
      .snapshotChanges()
      .pipe(
        map((changes) => {
          return changes.map((doc) => {
            let data = doc.payload.doc.data() as any;
            data.id = doc.payload.doc.id;
            return data;
          });
        })
      );
  }

  decrementGlobalRedeemCount(id) {
    const decrement = firestore.firestore.FieldValue.increment(-1);

    return this.afs
      .collection('discountCodes')
      .doc(id)
      .update({ globalRedeemCount: decrement });
  }

  //----------------- InventoryPages ----------------//

  updateInventoryPage(id: string, inventoryPage): Promise<void> {
    console.log("Inv page update")
    //updates a page in inventory pages
    inventoryPage['lastModified'] = new Date();
    return this.afs.collection('inventoryPages').doc(id).update(inventoryPage);
  }

  async addInventoryPage(title: any, isDefault: boolean) { //Adds new inventory page to inventory pages list
    let newPage = await this.afs.collection('inventoryPages').add({
      title: title,
      widgetList: [],
      defaultLayout: true,
      companyID: this.currentUserService.currentUser.companyId,
      dateCreated: new Date(), isDefault: isDefault, lastModified: new Date(), isActive: true
    })

    // else {
    //   this.afs.collection('inventoryPages').add({
    //     title: title,
    //     widgetList: [],
    //     defaultLayout: false,
    //     companyID: this.currentUserService.currentUser.companyId,
    //     dateCreated: new Date(), isDefault: false, lastModified: new Date(), isActive: true

    //   });
    // }
    return newPage;
  }

  async getInventoryPageById(id: string): Promise<any> {
    //Gets widgets list from remote and sets local widget list
    let thing;
    await this.afs
      .collection('inventoryPages')
      .doc(id)
      .ref.get()
      .then((res) => {
        thing = res.data();
      });
    thing.id = id;
    return thing;
  }

  getAllInventoryPage() {
    let ref = this.afs
      .collection('inventoryPages')
      .ref;
    return ref.get().then((res) => {
      return res.docs.map((r) => {
        let data = r.data() as any;
        data.id = r.id;
        return data;
      });
    });
  }

  deleteInventoryPage(id: string) {
    //Deletes inventory page from inventory page list
    return this.afs.collection('inventoryPages').doc(id).update({ isActive: false });
  }



  //--------------- Product Group Pages ----------------//

  // Atomically reassigns several product groups to layouts. A bulk reassignment
  // is one user action, so it must not half-apply: with individual writes a
  // mid-way failure left some groups moved and the rest not, while the UI
  // reported the whole operation as failed.
  batchUpdateGroupInventoryPageIds(changes: { groupId: string, inventoryPageId: string }[]): Promise<void> {
    if (!changes.length) {
      return Promise.resolve();
    }

    // Firestore caps a batch at 500 writes. Splitting across batches would give
    // back exactly the partial-application problem this method exists to remove,
    // so refuse instead of silently losing atomicity.
    if (changes.length > 500) {
      return Promise.reject(
        new Error('Cannot reassign more than 500 product groups at once.')
      );
    }

    const batch = this.afs.firestore.batch();
    const lastModified = new Date();

    changes.forEach(({ groupId, inventoryPageId }) => {
      const ref = this.afs.collection('productGroup').doc(groupId).ref;
      batch.update(ref, { inventoryPageId, lastModified });
    });

    return batch.commit();
  }

  // Updates any product groups attached to an inventory page
  updateProductGroupInventoryPageId(id: string, inventoryPageId: string) {
    console.log('updateProductInvId');
    return this.afs
      .collection('productGroup')
      .doc(id)
      .update({ inventoryPageId: inventoryPageId, lastModified: new Date() });
  }

  // Gets all product groups attached to an inv page
  searchProductGroupsAttachedToInvPage(invId) {
    let query = this.afs
      .collection('productGroup')
      .ref.where('inventoryPageId', '==', invId)
      .where('isActive', '==', true);
    return query.get().then((docs) => {
      return docs.docs.map((doc) => {
        let data = doc.data() as any;
        data.id = doc.id;
        return data;
      });
    });
  }

  getProductGroupById(id: string): Promise<any> {
    return this.afs
      .collection('productGroup')
      .doc(id)
      .ref.get()
      .then((doc) => {
        const data = doc.data() as any;
        data.id = doc.id;
        return data;
      });
  }

  getProductLocations(): Observable<any> {
    return this.afs
      .collection('productLocations', (ref) =>
        ref.where(
          'companyID',
          '==',
          this.currentUserService.currentUser.companyId
        )
      )
      .snapshotChanges()
      .pipe(
        map((changes) => {
          return changes.map((action) => {
            let data = action.payload.doc.data();
            data['id'] = action.payload.doc.id;
            return data;
          });
        })
      );
  }

  getProductLocationsWithParamCompanyID(companyID: string): Observable<any> {
    return this.afs.collection('productLocations', ref => ref
      .where('companyID', '==', companyID))
      .snapshotChanges().pipe(map(changes => {
        return changes.map(action => {
          let data = action.payload.doc.data()
          data['id'] = action.payload.doc.id
          return data
        })
      }));
  }

  async getLocationById(id: string): Promise<any> {
    //Gets a widget from widgets list
    // return this.widgets[id];
    let thing;
    await this.afs
      .collection('widgets')
      .doc(id)
      .ref.get()
      .then((res) => {
        thing = res.data();
      });
    return thing;
  }

  // getAllProductGroupForCompany(): Observable<any> { //Gets entire list of invenotry pages
  //   console.log("COMPANY ID")
  //   console.log(this.currentUserService.currentUser.companyId)
  //   return this.afs.collection('productGroups', ref => ref
  //     .where('companyID', '==', this.currentUserService.currentUser.companyId))
  //     .snapshotChanges().pipe(map(changes => {
  //       return changes.map(action => {
  //         let data = action.payload.doc.data()
  //         data['id'] = action.payload.doc.id
  //         return data;
  //       })
  //   }));
  // }



  // // get all updates to this template ID
  // getTemplateByID(id): Observable<any> { // subscribe to bookingTemplate collection updates - by companyID
  //   console.log(id)
  //   return this.afs.collection<any>('bookingTemplates').doc(id).snapshotChanges()
  // }

  // getProductGroupPages(): Observable<any> { //Gets entire list of widgets
  //   return this.afs.collection('productGroup', ref => ref
  //     .where('companyID', '==', this.currentUserService.currentUser.companyId))
  //     .snapshotChanges().pipe(map(changes => {
  //       return changes.map(action => {
  //         let data = action.payload.doc.data()
  //         data['id'] = action.payload.doc.id
  //         return data
  //       })
  //     }));
  // }

  // Gets all product groups attached to an inv page
  // getSizeTypesFromProductGroup(groupID) {
  //   let query = this.afs.collection('productGroup').ref.where('inventoryPageId', '==', invId)
  //   return query.get().then(docs => {
  //     return docs.docs.map(doc => {
  //       let data = doc.data() as any
  //       data.id = doc.id
  //       return data
  //     })
  //   })
  // }

  // getSizeTypesFromID(ID): Observable<any> { //Gets entire list of widgets
  //   return this.afs.collection('productSizeTypes', ref => ref
  //     .where('companyID', '==', this.currentUserService.currentUser.companyId))
  //     .snapshotChanges().pipe(map(changes => {
  //       return changes.map(action => {
  //         let data = action.payload.doc.data()
  //         data['id'] = action.payload.doc.id
  //         return data
  //     })
  //   }));
  // }

  /**
   * @description Creates a new document in productSizes collection from a valid CustomSize class object
   * @author Dustin Chen
   * @date 04/18/2023
   * @param {*} sizeObject object with following fields: companyId:string, productType:string, size:string, isActive:boolean, created?: Date, id?:string
   * @return {*} the auto-generated docID of the newly created size document
   */
  newCustomSize(sizeObject: ProductSizeType) {
    return this.afs
      .collection('productSizes')
      .add(sizeObject)
      .then((docRef) => {
        return docRef.id;
      });
  }

  /**
   * @description Handle reading from productSizes collection
   * @author Dustin Chen
   * @date 04/10/2023
   * @param {string} companyId companyId from currentUser; if this is the only parameter passed then the function will return all sizes matching the company
   * @param {string} [type] additional field to filter by: 'productType' or 'size'
   * @param {string} [id] for 'productType': title of list (string), for 'size': firebase docID
   * @param {*} [sortOrder]
   * @return {*}
   */
  async getCustomSizes(
    companyId: string,
    type?: string,
    id?: string,
    sortOrder?
  ) {
    let ref = await this.afs
      .collection('productSizes')
      .ref.where('isActive', '==', true)
      .where('companyId', '==', companyId);
    let snapshot;
    if (type) {
      switch (type) {
        case 'productType':
          snapshot = await ref
            .where('productType', '==', id)
            .orderBy('sortOrder')
            .get();
          break;
        case 'size':
          snapshot = await ref.where('__name__', '==', id).get();
          break;
        default:
          throw new Error(
            'Error getting sizes from firebase, unable to access attribute "' +
            type +
            '"'
          );
      }
    } else {
      snapshot = await ref.orderBy('productType').orderBy('sortOrder').get();
    }
    if ((await snapshot).empty) {
      throw new Error('No documents matching companyId');
    }
    return snapshot.docs.map((response) => {
      let data = response.data() as any;
      data.id = response.id;
      return data;
    });
  }

  // Created by Morgan
  getProductSizesForCompany(): Observable<any> {
    //Observable to subscribe to
    return this.afs
      .collection(
        'productSizes',
        (ref) =>
          ref
            .where('isActive', '==', true)
            .where(
              'companyId',
              '==',
              this.currentUserService.currentUser.companyId
            )
        //.orderBy('productType')
        //.orderBy('sortOrder')
      )
      .snapshotChanges()
      .pipe(
        map((changes) => {
          return changes.map((action) => {
            let data = action.payload.doc.data() as ProductSizeType;
            data.id = action.payload.doc.id;
            return data;
          });
        })
      );
  }

  // Created by Morgan
  getProductSizesWithCompanyID(companyID): Observable<any> {
    //Observable to subscribe to
    return this.afs
      .collection(
        'productSizes',
        (ref) =>
          ref
            .where('isActive', '==', true)
            .where(
              'companyId',
              '==',
              companyID
            )
        //.orderBy('productType')
        //.orderBy('sortOrder')
      )
      .snapshotChanges()
      .pipe(
        map((changes) => {
          return changes.map((action) => {
            let data = action.payload.doc.data() as ProductSizeType;
            data.id = action.payload.doc.id;
            return data;
          });
        })
      );
  }

  /**
   * @description Read and emit changes to productSizes collection (a little redundant with async getCustomSizes, look to combine the two functions)
   * @author Dustin Chen
   * @date 04/14/2023
   * @param {string} companyId companyId from currentUser
   * @param {string} [type]
   * @param {string} [id]
   * @param {*} [sortOrder]
   * @return {*}  {Observable<CustomSize[]>}
   */
  getCustomSizesObservable(
    companyId: string,
    type?: string,
    id?: string,
    sortOrder?
  ): Observable<ProductSizeType[]> {
    //Observable to subscribe to
    return this.afs
      .collection(
        'productSizes',
        (ref) =>
          ref.where('isActive', '==', true).where('companyId', '==', companyId)
        //.orderBy('productType')
        //.orderBy('sortOrder')
      )
      .snapshotChanges()
      .pipe(
        map((changes) => {
          return changes.map((action) => {
            let data = action.payload.doc.data() as ProductSizeType;
            data.id = action.payload.doc.id;
            return data;
          });
        })
      );
    // let ref = this.afs.collection('productSizes').ref.where('isActive','==',true).where('companyId','==',companyId);
    // let snapshot;
    // if(type) {
    //   switch(type) {
    //     case 'productType':
    //       snapshot = ref.where('productType','==',id).get();
    //       break;
    //     case 'size':
    //       snapshot = ref.where('__name__', '==', id).get();
    //       break;
    //     default:
    //       throw new Error('Error getting sizes from firebase, unable to access attribute "' + type + '"');
    //   }
    // } else {
    //     snapshot = ref.orderBy('productType').orderBy('sortOrder').snapshotChanges;
    // }
    // if((snapshot).empty) { throw new Error("No documents matching companyId"); }
    // return snapshot.docs.map(response => { let data = response.data() as any; data.id = response.id; return data});
  }

  getCustomSizesObservableDefault(defaultID: string): Observable<ProductSizeType[]> {
    //Observable to subscribe to
    return this.afs
      .collection('productSizes', (ref) =>
        ref.where('isActive', '==', true).where('productType', '==', defaultID)
      )
      .snapshotChanges()
      .pipe(
        map((changes) => {
          return changes.map((action) => {
            let data = action.payload.doc.data() as ProductSizeType;
            data.id = action.payload.doc.id;
            return data;
          });
        })
      );
  }
  getCustomSizesPromise(defaultID: string): Promise<ProductSizeType[]> {
    //Observable to subscribe to
    let ref = this.afs
      .collection('productSizes')
      .ref.where('isActive', '==', true)
      .where('productType', '==', defaultID);
    return ref.get().then((res) => {
      return res.docs.map((r) => {
        let data = r.data() as ProductSizeType;
        data.id = r.id;
        return data;
      });
    });
  }

  /**
   * @description Handle changes to productSizes collection
   * @author Dustin Chen
   * @date 04/10/2023
   * @param {string} companyId companyId from currentUser
   * @param {string} type The field to update: 'productType', 'size', 'sortOrder', 'isActive'
   * @param {string} id for 'productType': original name of list; for 'size','sortOrder','isActive': firebase docID,
   * @param {*} updatedData for 'productType','size': new name (string), for 'sortOrder': new index (numbered from 0), for 'isActive': either 'delete' or 'restore'
   */
  async updateCustomSizes(
    companyId: string,
    type: string,
    id: string,
    updatedData: any
  ) {
    let ref = await this.afs
      .collection('productSizes')
      .ref.where('companyId', '==', companyId);
    let snapshot;
    let docRef = await this.afs.collection('productSizes').ref.doc(id);
    switch (type) {
      case 'productType':
        snapshot = await ref
          .where('isActive', '==', true)
          .where('productType', '==', id)
          .get()
          .then((response) => {
            let batch = this.afs.firestore.batch();
            response.docs.forEach((doc) => {
              const docRef = this.afs
                .collection('productSizes')
                .doc(doc.id).ref;
              batch.update(docRef, { productType: updatedData });
            });
            batch.commit().then(() => {
              console.log(`updated all documents inside ${id}`);
            });
          });
        break;
      case 'size':
        break;
      case 'sortOrder':
        if (!docRef) throw new Error('No Documents matching id ' + id);
        else {
          docRef.update({ sortOrder: updatedData });
          //console.log("updated sort order",id,docRef,updatedData);
        }
        break;
      case 'isActive':
        if (!docRef) throw new Error('No Documents matching id ' + id);
        else {
          if (updatedData == 'delete')
            docRef.update({ isActive: false, deleted: new Date() });
          if (updatedData == 'restore') docRef.update({ isActive: true });
        }
        break;
      default:
        console.log(
          'Error writing to firebase, attribute "' + type + '" doesn\'t exist'
        );
    }
  }

  //Begin refactored functions for 2 collections (productSizes & productSizeTypes)

  getCustomSizeTypesObservable(companyId: string): Observable<any> {
    return this.afs
      .collection(
        'productSizeTypes',
        (ref) =>
          ref
            .where('isActive', '==', true)
            .where('companyId', '==', companyId)
            .orderBy('productType') //why does this break the observable?? (solved: because I hadn't created a firebase composite index for this query yet >.<)
      )
      .snapshotChanges()
      .pipe(
        map((changes) => {
          return changes.map((action) => {
            let data = action.payload.doc.data() as any;
            data.id = action.payload.doc.id;
            return data;
          });
        })
      );
  }
  getCustomSizeTypesPromise(companyId: string): Promise<any> {
    let ref = this.afs
      .collection('productSizeTypes')
      .ref.where('isActive', '==', true)
      .where('companyId', '==', companyId)
      .orderBy('productType');
    return ref.get().then((res) => {
      return res.docs.map((r) => {
        let data = r.data() as any;
        data.id = r.id;
        return data;
      });
    });
  }
  getCustomSizePromise(companyId: string): Promise<any> {
    let ref = this.afs
      .collection('productSizes')
      .ref.where('isActive', '==', true)
      .where('companyId', '==', companyId);
    return ref.get().then((res) => {
      return res.docs.map((r) => {
        let data = r.data() as any;
        data.id = r.id;
        return data;
      });
    });
  }
  getCustomSizeDefaultPromise(companyId: string): Promise<ProductSizeType[]> {
    return this.getCustomSizeTypesPromiseDefault(companyId);
  }
  getCustomSizeTypesObservableDefault(companyId: string = this.currentUserService.currentUser.companyId): Observable<ProductSizeType[]> {
    return this.afs
      .collection(
        'productSizeTypes',
        (ref) =>
          ref.where('isActive', '==', true)
            .where('isDefault', '==', true)
            .where('companyId', '==', companyId)
      )
      .snapshotChanges()
      .pipe(
        switchMap((changes) => {
          const companyDefaultSizeTypes = changes.map((action) => {
            const data = action.payload.doc.data() as ProductSizeType;
            data.id = action.payload.doc.id;
            return data;
          });

          if (companyDefaultSizeTypes.length > 0) {
            return of(companyDefaultSizeTypes);
          }

          return this.afs
            .collection('productSizeTypes')
            .doc(GLOBAL_DEFAULT_SIZE_TYPE_ID)
            .snapshotChanges()
            .pipe(
              map((action) => {
                if (!action.payload.exists) {
                  return [];
                }

                const data = action.payload.data() as ProductSizeType;
                data.id = action.payload.id;
                return [data];
              })
            );
        })
      );
  }

  async getCustomSizeTypesPromiseDefault(companyId: string = this.currentUserService.currentUser.companyId): Promise<ProductSizeType[]> {
    const companyDefaultRef = this.afs
      .collection('productSizeTypes')
      .ref.where('isActive', '==', true)
      .where('isDefault', '==', true)
      .where('companyId', '==', companyId);
    const companyDefault = await companyDefaultRef.get();

    if (!companyDefault.empty) {
      return companyDefault.docs.map((r) => {
        const data = r.data() as ProductSizeType;
        data.id = r.id;
        return data;
      });
    }

    const globalDefaultDoc = await this.afs
      .collection('productSizeTypes')
      .doc(GLOBAL_DEFAULT_SIZE_TYPE_ID)
      .ref
      .get();

    if (!globalDefaultDoc.exists) {
      return [];
    }

    const globalDefault = globalDefaultDoc.data() as ProductSizeType;
    globalDefault.id = globalDefaultDoc.id;
    return [globalDefault];
  }

  async createDefaultSizeTypeForCompany(companyId: string): Promise<string> {
    const existingDefault = await this.afs
      .collection('productSizeTypes')
      .ref.where('companyId', '==', companyId)
      .where('isDefault', '==', true)
      .limit(1)
      .get();

    if (!existingDefault.empty) {
      return existingDefault.docs[0].id;
    }

    const globalDefaultRef = this.afs
      .collection('productSizeTypes')
      .doc(GLOBAL_DEFAULT_SIZE_TYPE_ID)
      .ref;
    const globalDefaultDoc = await globalDefaultRef.get();

    if (!globalDefaultDoc.exists) {
      throw new Error('Source default size type does not exist.');
    }

    const globalDefault = globalDefaultDoc.data() as ProductSizeType;
    const globalDefaultSizes = await this.getCustomSizesPromise(globalDefaultDoc.id);
    const sizeTypeDocRef = this.afs.collection('productSizeTypes').ref.doc();
    const sizeTypeId = sizeTypeDocRef.id;
    const batch = this.afs.firestore.batch();
    const sortOrder: string[] = [];

    globalDefaultSizes.forEach((size) => {
      const sizeDocRef = this.afs.collection('productSizes').ref.doc();
      sortOrder.push(sizeDocRef.id);
      batch.set(sizeDocRef, {
        companyId,
        productType: sizeTypeId,
        size: size.size,
        isActive: true,
        created: new Date(),
      });
    });

    batch.set(sizeTypeDocRef, {
      companyId,
      productType: globalDefault.productType || 'Default',
      sortOrder,
      isActive: true,
      isDefault: true,
      created: new Date(),
      editable: true,
    });

    await batch.commit();

    return sizeTypeId;
  }

  // Created by morgan
  getCustomSizeTypesObservableNoParams(): Observable<any> {
    return this.afs
      .collection('productSizeTypes', (ref) =>
        ref
          .where('isActive', '==', true)
          .where(
            'companyId',
            '==',
            this.currentUserService.currentUser.companyId
          )
      )
      .snapshotChanges()
      .pipe(
        map((changes) => {
          return changes.map((action) => {
            let data = action.payload.doc.data() as any;
            data.id = action.payload.doc.id;
            return data;
          });
        })
      );
  }

  // Created by morgan
  getCustomSizeTypesPromiseWithCompanyID(companyId: string): Promise<any> {
    let ref = this.afs
      .collection('productSizeTypes')
      .ref.where('isActive', '==', true)
      .where('companyId', '==', companyId)
    return ref.get().then((res) => {
      return res.docs.map((r) => {
        let data = r.data() as any;
        data.id = r.id;
        return data;
      });
    });
  }

  /**
   * @description Creates a new document in productSizeTypes collection
   * @author Dustin Chen
   * @date 04/18/2023
   * @param {*} sizeTypeObject attributes this object should have: companyId, productType, sortOrder, isActive, created
   *                            // Will turn this into a model interface if 2 collection conversion is successful
   * @return {*} the docID of the newly generated document
   */
  newCustomSizeType(sizeTypeObject: any): Promise<string> {
    return this.afs
      .collection('productSizeTypes')
      .add(sizeTypeObject)
      .then((docRef) => {
        return docRef.id;
      });
  }

  newProductSize(sizeObject: ProductSizeType, productType: string): Promise<string> {
    return this.afs
      .collection('productSizes')
      .add(sizeObject)
      .then((docRef) => {
        return docRef.id;
      });
  }

  /**
   * @description 2 collection version of updateCustomSizes to handle changes to both productSizes and productSizeTypes collections
   *              Calls to this function should be either in a try-catch block or followed by .catch(err => console.error(err))
   *              (to prevent uncaught errors messages from bubbling up)
   * @author Dustin Chen
   * @date 04/18/2023
   * @param {string} companyId companyId from currentUser
   * @param {string} action the field that will be changed: 'productType', 'size', 'sortOrder', 'isActiveList', 'isActiveSize'
   * @param {string} id for 'productType', 'sortOrder','isActiveList': firebase docID of the list in productSizeTypes collection;
   *                    for 'size','isActiveSize': firebase docID of the size in productSizes collection
   * @param {*} updatedData for 'productType', 'size': new name (string); for 'sortOrder': sorted list of sizeDoc IDs (Array);
   *                        for 'isActiveList', 'isActiveSize': either "delete" or "restore"
   */
  async updateProductSizes(
    companyId: string,
    action: string,
    id: string,
    updatedData: string | Array<string> | any
  ) {
    if (typeof updatedData === 'object' && updatedData !== null) {
      updatedData['lastModified'] = new Date();
    }
    console.log(updatedData)
    let productTypeDocRef = await this.afs
      .collection('productSizeTypes')
      .ref.doc(id);
    let productSizesRef = await this.afs
      .collection('productSizes')
      .ref.where('companyId', '==', companyId);
    let sizeDocRef = await this.afs.collection('productSizes').ref.doc(id);
    let productsRef = await this.afs.collection('products').ref.doc(id);
    let snapshot;
    switch (action) {
      case 'productType':
        if (!productTypeDocRef)
          throw new Error('No list documents with id ' + id);
        else {
          productTypeDocRef.update({ productType: updatedData as string, lastModified: new Date() });
        }
        break;
      case 'size':
        if (!sizeDocRef) throw new Error('No size documents with id ' + id);
        else {
          sizeDocRef.update({ size: updatedData as string, lastModified: new Date() });
          console.log('Updated size: ', id, updatedData);
        }
        break;
      case 'sortOrder':
        if (!productTypeDocRef)
          throw new Error('No list documents with id ' + id);
        else {
          productTypeDocRef.update({ sortOrder: updatedData as Array<string>, lastModified: new Date() });
          console.log('Updated sortOrder: ', id, updatedData);
        }
        break;
      case 'isActiveList':
        if (!productTypeDocRef)
          throw new Error('No list documents with id ' + id);
        else {
          if (updatedData == 'delete') {
            productTypeDocRef.update({ isActive: false, deleted: new Date(), lastModified: new Date() });
            console.log('list deleted', id);
          }
          if (updatedData == 'restore')
            productTypeDocRef.update({ isActive: true, lastModified: new Date() });
        }
        break;
      case 'isActiveSize':
        // Set 'isActive' field on productSizes document
        // Also add or remove from associated productSizeTypes sortOrder array
        // Create an 'inactiveSizes' array to keep ids linked up in case of restore
        if (!sizeDocRef) throw new Error('No size documents with id ' + id);
        else {
          if (updatedData == 'delete')
            sizeDocRef.update({ isActive: false, deleted: new Date(), lastModified: new Date() });
          if (updatedData == 'restore') sizeDocRef.update({ isActive: true, lastModified: new Date() });
        }
        let sizeDocData: ProductSizeType = (await (
          await sizeDocRef.get()
        ).data()) as ProductSizeType;
        let productTypeId = sizeDocData.productType;
        productTypeDocRef = await this.afs
          .collection('productSizeTypes')
          .ref.doc(productTypeId);
        if (!productTypeDocRef)
          throw new Error('No list documents with id ' + productTypeId);
        else {
          if (updatedData == 'delete') {
            productTypeDocRef.update({
              sortOrder: firestore.firestore.FieldValue.arrayRemove(id),
              inactiveSizes: firestore.firestore.FieldValue.arrayUnion(id),
              lastModified: new Date()
            });
            console.log('removed ' + id + ' from sortOrder Array');
          }
          if (updatedData == 'restore') {
            productTypeDocRef.update({
              sortOrder: firestore.firestore.FieldValue.arrayUnion(id),
              inactiveSizes: firestore.firestore.FieldValue.arrayRemove(id),
              lastModified: new Date()
            });
          }
        }
        break;
      case 'product':
        if (!productsRef) throw new Error('No list documents with id ' + id);
        else {
          productsRef.update(updatedData);
        }

        console.log('updatedData -->', updatedData);
        console.log('id -->', id);

        break;
      default:
        throw new Error('Error writing to firebase');
    }

    // let ref = await this.afs.collection('productSizes').ref.where('companyId','==',companyId);
    // let snapshot;
    // let docRef = await this.afs.collection('productSizes').ref.doc(id);
    // switch(type) {
    //   case 'productType':
    //     snapshot = await ref.where('isActive','==',true).where('productType','==',id).get().then(response => {
    //       let batch = this.afs.firestore.batch()
    //       response.docs.forEach((doc) => {
    //         const docRef = this.afs.collection('productSizes').doc(doc.id).ref;
    //         batch.update(docRef, {productType: updatedData})
    //       })
    //       batch.commit().then(() => {console.log(`updated all documents inside ${id}`);})
    //     })
    //     break;
    //   case 'size':
    //     break;
    //   case 'sortOrder':
    //     if (!docRef) throw new Error("No Documents matching id " + id);
    //     else {
    //       docRef.update({sortOrder: updatedData});
    //       //console.log("updated sort order",id,docRef,updatedData);
    //     }
    //     break;
    //   case 'isActive':
    //     if (!docRef) throw new Error("No Documents matching id " + id);
    //     else {
    //       if (updatedData == 'delete') docRef.update({'isActive': false, 'deleted': new Date()});
    //       if (updatedData == 'restore') docRef.update({'isActive': true});
    //     }
    //       break;
    //   default:
    //     console.log('Error writing to firebase, attribute "' + type + '" doesn\'t exist')
    // }
  }

  updateTitleandIdslayouts(id: any, title: string): Promise<void> {
    if (title !== undefined) {
      return this.afs
        .collection('inventoryPages')
        .doc(id)
        .update({ title: title });
    } else {
      return Promise.reject('error');
    }
  }



  updateSavedWidgetName(id: any, title: string): Promise<void> {
    if (title !== undefined) {
      return this.afs
        .collection('widgets')
        .doc(id)
        .update({ widgetName: title });
    } else {
      return Promise.reject('error');
    }
  }
}



function trunc(x: number, positions: number) {
  let s = x.toString();
  let l = s.length;
  let decimalLength = s.indexOf('.') + 1;
  let numStr = s.substr(0, decimalLength + positions);
  return Number(numStr);
}
